import React, { FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMedia } from 'react-use';

import dayjs from 'dayjs';

import { ButtonTypes } from 'components/Button';
import { RequestTimeOffType } from 'modules/Common/types/hrModuleTypes';
import {
	CalendarEmojisByTypes,
	MOBILE_VIEW_BREAKPOINT,
} from 'modules/HR/constants/HRModuleConstants.constants';
import { Routes } from 'modules/HR/routes/types';
import { GenericType } from 'types';
import { fetchEmployeePhotoWithCredentials, formatDateRange } from 'utils/helpers';

import UserIcon from '../../../Common/components/UserIcon';
import DeclineRequestTimeOffModal from '../DeclineRequestTimeOffModal';
import HRThemedButton from '../HRThemedButton';

import { RequestColorsByTypes } from './HRRequest.constants';
import { getRequestStatus } from './HRRequestComponent.helpers';
import { Styled } from './HRRequestComponent.styled';

type HRRequestComponentProps = {
	employeeRequest: RequestTimeOffType;
	isEmployeeView?: boolean;
	isInBlockView?: boolean;
	isShortView?: boolean;
	loading?: GenericType;
	declineEmployeeTimeOffRequest?: (
		values: { requestId: number; comment: string | null },
		cb?: () => void,
	) => void;
	approveEmployeeTimeOffRRequest?: (requestId: number) => void;
};

export const HRRequestComponent: FC<HRRequestComponentProps> = ({
	employeeRequest,
	isEmployeeView = false,
	isInBlockView = false,
	isShortView = false,
	loading,
	declineEmployeeTimeOffRequest,
	approveEmployeeTimeOffRRequest,
}) => {
	const [requestId, setRequestId] = useState<number | null>(null);
	const dateFormat = 'MMM DD, YYYY';
	const requestDates = formatDateRange(employeeRequest.startDate, employeeRequest.endDate);
	const isMobile = useMedia(`(max-width: ${MOBILE_VIEW_BREAKPOINT})`);
	const navigate = useNavigate();

	const handleApproveRequest = () => {
		approveEmployeeTimeOffRRequest && approveEmployeeTimeOffRRequest(employeeRequest.id);
	};

	const handleDeclineRequest = useCallback((id: number) => {
		setRequestId(id);
	}, []);

	const handleCloseDeclineModal = useCallback(() => {
		setRequestId(null);
	}, []);

	const handleNavigateToProfile = () => {
		!isMobile && navigate(`${Routes.HRModule}${Routes.Employee}/${employeeRequest.employee.id}`);
	};

	const requestStatus = getRequestStatus(
		employeeRequest.approved,
	) as keyof typeof RequestColorsByTypes;

	return (
		<Styled.Root isInBlockView={isInBlockView}>
			{!isEmployeeView && !isInBlockView && (
				<Styled.Header>
					<Styled.EmployeeData onClick={handleNavigateToProfile}>
						<UserIcon
							imageId={employeeRequest.employee.photoRef}
							backgroundColor='grey'
							fullName={`${employeeRequest.employee.fullName}`}
							fetchAndSetImageHelper={fetchEmployeePhotoWithCredentials}
						/>
						<Styled.EmployeeName>{`${employeeRequest.employee.fullName}`}</Styled.EmployeeName>
					</Styled.EmployeeData>
					<Styled.RequestDate>
						{dayjs(employeeRequest.created).format(dateFormat)}
					</Styled.RequestDate>
				</Styled.Header>
			)}
			<Styled.RequestMainContent isEmployeeView={isEmployeeView} isInBlockView={isInBlockView}>
				<Styled.RequestInfoTopRow>
					<Styled.RequestInfoTypeDatesWrap>
						<Styled.RequestType>{`${
							CalendarEmojisByTypes[employeeRequest.type]
						} ${employeeRequest.type.toLocaleLowerCase()}`}</Styled.RequestType>
						<Styled.RequestDates isShortView={isShortView}>{requestDates}</Styled.RequestDates>
					</Styled.RequestInfoTypeDatesWrap>
					{requestStatus && (
						<Styled.RequestApproveStatusBadge
							color={RequestColorsByTypes[requestStatus].color}
							bgColor={RequestColorsByTypes[requestStatus].bgColor}
							borderColor={RequestColorsByTypes[requestStatus].borderColor}
						>
							{requestStatus}
						</Styled.RequestApproveStatusBadge>
					)}
				</Styled.RequestInfoTopRow>
				{!isShortView && (
					<Styled.RequestComment>
						{!isInBlockView && <span>Comments</span>}
						{employeeRequest.description}
					</Styled.RequestComment>
				)}
			</Styled.RequestMainContent>
			{!isEmployeeView && employeeRequest.approved === null && (
				<Styled.ButtonBox>
					<HRThemedButton
						type='button'
						buttonType={ButtonTypes.secondary}
						onClick={() => handleDeclineRequest(employeeRequest.id)}
					>
						Decline
					</HRThemedButton>
					<HRThemedButton
						type='submit'
						buttonType={ButtonTypes.primary}
						onClick={handleApproveRequest}
						disabled={!!loading?.approveTimeOffRequestLoad}
					>
						Approve
					</HRThemedButton>
				</Styled.ButtonBox>
			)}
			<DeclineRequestTimeOffModal
				isOpen={requestId}
				loading={!!loading?.declineTimeOffRequestLoad}
				onApprove={declineEmployeeTimeOffRequest}
				onCancel={handleCloseDeclineModal}
			/>
		</Styled.Root>
	);
};
